import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FiltersService from "../services/api/filters/filters.service";
import {
  CERTIFICATIONS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { FilterProps } from "../types/AppTypes";
import { useNotification } from "./notification.hook";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import CertificationsService from "../services/api/certifications/Certifications.service";
import {
  Certification,
  CertificationRequest,
} from "../types/api/certifications.type";
import { useEffect } from "react";

const queryKeyGetCertifications = "get-certifications";

export const useFormationCertificationFilters = () => {
  const { notification } = useNotification();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["get-filters-certifications"],
    queryFn: FiltersService.listForCertifications,
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading };
};

interface useFormationCertificationProps {
  filtersCertifications: FilterProps[];
  paginationCertifications: number;
  searchDebouncedCertification: string;
}

export const useFormationCertification = ({
  filtersCertifications,
  paginationCertifications,
  searchDebouncedCertification,
}: useFormationCertificationProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      queryKeyGetCertifications,
      filtersCertifications,
      paginationCertifications,
      searchDebouncedCertification,
    ],
    queryFn: () =>
      CertificationsService.list(
        CERTIFICATIONS_PAGE_SIZE,
        paginationCertifications,
        [
          ...filtersCertifications,
          ...(searchDebouncedCertification
            ? [{ key: "search", value: [searchDebouncedCertification] }]
            : []),
        ],
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useDeleteFormationCertification = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: number) => CertificationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyGetCertifications] });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting formation certification: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useUpdateFormationCertification = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      body,
      record,
    }: {
      body: CertificationRequest;
      record?: Certification;
    }) => {
      if (record) {
        return CertificationsService.update(record.id, body);
      } else {
        return CertificationsService.add(body);
      }
    },
    onSuccess: (_, { record }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeyGetCertifications] });
      const message = record
        ? DATA_UPDATED_SUCCESS_LABEL
        : DATA_ADDED_SUCCESS_LABEL;
      notification.success(message, undefined, "toast");
    },
    onError: (error) => {
      console.error(`${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};
